import { useMemo, useState, useEffect } from 'react';
import { Users, Search, Info, GraduationCap, MessageCircle, Filter } from 'lucide-react';
import { MobileProfessorAlunoPainel } from './MobileProfessorAlunoPainel';
import { AvisarResponsavelModal } from './AvisarResponsavelModal';
import {
  TURNO_LABEL_ALUNOS,
  useMeusAlunosProfessorPage,
  type AlunoListaProfessor,
} from './useMeusAlunosProfessorPage';
import './alunos-professor-desktop.css';
import './professor-dashboard-desktop.css';

function getInitials(nome: string): string {
  if (!nome) return '?';
  const parts = nome.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function hasFotoReal(fotoUrl?: string) {
  if (!fotoUrl) return false;
  const url = fotoUrl.toLowerCase();
  return !url.includes('i.pravatar.cc') && !url.includes('ui-avatars.com');
}

export interface MeusAlunosDesktopProps {
  initialAlunoId?: string | null;
}

export function MeusAlunosDesktop({ initialAlunoId }: MeusAlunosDesktopProps) {
  const { alunos, turmasOpcoes, turnosDisponiveis, isLoading, totalTurmasGrade } =
    useMeusAlunosProfessorPage();

  const [busca, setBusca] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroTurno, setFiltroTurno] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(initialAlunoId ?? null);
  const [avisoAluno, setAvisoAluno] = useState<{ id: string; nome: string } | null>(null);

  const filtered = useMemo(() => {
    let list = alunos;
    const q = busca.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) =>
          a.nome.toLowerCase().includes(q) ||
          (a.matricula || '').toLowerCase().includes(q) ||
          a.turmaNome.toLowerCase().includes(q),
      );
    }
    if (filtroTurma) list = list.filter((a) => a.turmaId === filtroTurma);
    if (filtroTurno) list = list.filter((a) => a.turmaTurno === filtroTurno);
    return list.sort((a, b) => {
      if (a.turmaNome !== b.turmaNome) return a.turmaNome.localeCompare(b.turmaNome, 'pt-BR');
      return (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
    });
  }, [alunos, busca, filtroTurma, filtroTurno]);

  useEffect(() => {
    if (initialAlunoId) setSelectedId(initialAlunoId);
  }, [initialAlunoId]);

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filtered.some((a) => a.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const grouped = useMemo(() => {
    const map = new Map<string, AlunoListaProfessor[]>();
    filtered.forEach((a) => {
      const key = a.turmaNome;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    });
    return map;
  }, [filtered]);

  const turmasComAlunos = turmasOpcoes.length;

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page alu-desk-page">
      <div className="dash-desk-root alu-desk-root">
        <div className="dash-desk-top alu-desk-top">
          <div className="dash-desk-greeting">
            <h1 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
              Meus alunos
            </h1>
            <p className="alu-desk-subtitle">
              {filtered.length} aluno{filtered.length !== 1 ? 's' : ''} nas suas turmas · somente consulta
            </p>
          </div>
          <Users size={18} style={{ color: '#2563eb', opacity: 0.85 }} aria-hidden />
        </div>

        <div className="alu-desk-metrics">
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
              <Users size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{alunos.length}</div>
              <div className="dash-desk-kpi-lbl">Alunos</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
              <GraduationCap size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{turmasComAlunos}</div>
              <div className="dash-desk-kpi-lbl">Turmas c/ alunos</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>
              <Filter size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{totalTurmasGrade}</div>
              <div className="dash-desk-kpi-lbl">Turmas na grade</div>
            </div>
          </div>
          <div className="dash-desk-kpi">
            <div className="dash-desk-kpi-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
              <MessageCircle size={15} />
            </div>
            <div>
              <div className="dash-desk-kpi-val">{filtered.length}</div>
              <div className="dash-desk-kpi-lbl">Na lista</div>
            </div>
          </div>
        </div>

        <div className="alu-desk-notice">
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Lista via <code style={{ fontSize: '0.65rem' }}>chamadas/turma/…/alunos</code> (sem{' '}
            <code style={{ fontSize: '0.65rem' }}>GET /alunos</code> da escola). Use o ícone de mensagem para{' '}
            <strong>avisar o responsável</strong>.
          </span>
        </div>

        <div className="alu-desk-toolbar">
          <div className="alu-desk-search-wrap">
            <Search size={14} />
            <input
              type="search"
              className="alu-desk-search"
              placeholder="Buscar nome, matrícula, turma…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <select
            className="alu-desk-filter"
            value={filtroTurma}
            onChange={(e) => setFiltroTurma(e.target.value)}
            aria-label="Filtrar turma"
          >
            <option value="">Todas turmas</option>
            {turmasOpcoes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </select>
          {turnosDisponiveis.length > 1 && (
            <select
              className="alu-desk-filter"
              value={filtroTurno}
              onChange={(e) => setFiltroTurno(e.target.value)}
              aria-label="Filtrar turno"
            >
              <option value="">Todos turnos</option>
              {turnosDisponiveis.map((t) => (
                <option key={t} value={t}>
                  {TURNO_LABEL_ALUNOS[t] || t}
                </option>
              ))}
            </select>
          )}
          <span className="alu-desk-count">{filtered.length} aluno(s)</span>
        </div>

        <div className="alu-desk-body">
          <div className="alu-desk-list-card">
            <div className="alu-desk-list-scroll">
              {isLoading ? (
                <p className="alu-desk-loading">Carregando alunos…</p>
              ) : filtered.length === 0 ? (
                <p className="alu-desk-loading">
                  Nenhum aluno nas suas turmas. Ajuste a busca ou peça vínculo na grade.
                </p>
              ) : (
                Array.from(grouped.entries()).map(([turmaNome, lista]) => (
                  <div key={turmaNome}>
                    <p className="alu-desk-group-label">
                      {turmaNome} · {lista.length}
                    </p>
                    {lista.map((a) => {
                      const active = selectedId === a.id;
                      return (
                        <div
                          key={a.id}
                          className={[
                            'alu-desk-list-item',
                            active ? 'alu-desk-list-item--active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <button
                            type="button"
                            style={{
                              display: 'flex',
                              gap: 8,
                              alignItems: 'center',
                              flex: 1,
                              minWidth: 0,
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                            onClick={() => setSelectedId(a.id)}
                          >
                            {hasFotoReal(a.fotoUrl) ? (
                              <img
                                src={a.fotoUrl}
                                alt=""
                                className="alu-desk-avatar alu-desk-avatar--img"
                              />
                            ) : (
                              <span
                                className={[
                                  'alu-desk-avatar',
                                  a.genero === 'FEMININO' ? 'alu-desk-avatar--f' : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                {getInitials(a.nome)}
                              </span>
                            )}
                            <span className="alu-desk-list-main">
                              <p className="alu-desk-list-name">{a.nome}</p>
                              <p className="alu-desk-list-meta">
                                {a.matricula || '—'}
                                {a.turmaTurno
                                  ? ` · ${TURNO_LABEL_ALUNOS[a.turmaTurno] || a.turmaTurno}`
                                  : ''}
                              </p>
                            </span>
                          </button>
                          <div className="alu-desk-list-aside">
                            <button
                              type="button"
                              className="alu-desk-aviso-btn"
                              title="Avisar responsável"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAvisoAluno({ id: a.id, nome: a.nome });
                              }}
                            >
                              <MessageCircle size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="alu-desk-detail-card">
            <div className="alu-desk-detail-scroll">
              {selectedId ? (
                <MobileProfessorAlunoPainel alunoId={selectedId} variant="panel" />
              ) : (
                <div className="alu-desk-detail-empty">
                  Selecione um aluno para ver frequência, notas, saúde e contato da família.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {avisoAluno && (
        <AvisarResponsavelModal
          open={!!avisoAluno}
          onClose={() => setAvisoAluno(null)}
          alunoId={avisoAluno.id}
          alunoNome={avisoAluno.nome}
        />
      )}
    </div>
  );
}
